import { Box, Checkbox, FormControl, InputLabel, ListItemText, MenuItem, OutlinedInput, Select } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';

import RadiusButton from '../../../../components/RadiusButton';

// Multi-select rows: only the checkbox shows the selection; !important beats the global .Mui-selected fill in index.scss
const checkboxOnlySelectedSx = {
    '&.Mui-selected': { backgroundColor: 'transparent !important', color: 'inherit !important' },
    '&.Mui-selected:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04) !important' },
};

// Filter row of the Dashboard (Date, Drive, Course, Batch, Venue + Go); filters come from useDashboardFilters.
// isSingleVenue: one venue at a time (Alert Details page lists the alerts of a single venue).
export const DashboardFilters = ({ filters, onGo, isSingleVenue = false }) => {

    const {
        allData,
        selectedDate,
        selectedDrive,
        driveOptions,
        courses,
        selectedCourseIds,
        batches,
        selectedBatch,
        venueOptions,
        selectedVenueIds,
        handleDateChange,
        handleDriveChange,
        handleCourseChange,
        handleBatchChange,
        handleVenueChange,
    } = filters;

    return (
        <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'repeat(auto-fit, minmax(150px, 1fr))', md: 'repeat(5, minmax(0, 1fr)) auto' },
            alignItems: 'center',
            gap: 1.5,
            mb: 2,
        }}>
            <DatePicker
                label='Date'
                format='DD-MM-YYYY'
                value={selectedDate}
                onChange={(date) => handleDateChange(date)}
                slotProps={{ textField: { size: 'small' }, field: { clearable: true } }}
                sx={{ width: '100%', minWidth: 0 }}
            />

            <FormControl className='form-field' sx={{ width: '100%', minWidth: 0 }}>
                <InputLabel id="driveName">Drive Name</InputLabel>
                <Select
                    labelId="driveName"
                    id="drive"
                    label='Drive Name'
                    name='drive'
                    size='small'
                    value={selectedDrive?.id ?? 0}
                    onChange={(e, event) => handleDriveChange(event.props.value)}
                >
                    {
                        driveOptions.map(
                            (drive) => <MenuItem key={drive.id} value={drive.id}>
                                {drive.driveName}
                            </MenuItem>
                        )
                    }
                </Select>
            </FormControl>

            <FormControl className='form-field' sx={{ width: '100%', minWidth: 0 }}>
                <InputLabel id="courseName" shrink>Course Name</InputLabel>
                <Select
                    labelId="courseName"
                    id="course"
                    label='Course Name'
                    name='course'
                    size='small'
                    multiple
                    displayEmpty
                    input={<OutlinedInput notched label='Course Name' />}
                    value={selectedCourseIds}
                    onChange={(e) => handleCourseChange(e.target.value)}
                    renderValue={(ids) => ids.length
                        ? courses.filter((course) => ids.includes(course.id)).map((course) => course.courseName).join(', ')
                        : 'None'}
                    MenuProps={{ PaperProps: { sx: { maxHeight: 320 } } }}
                >
                    {courses.length <= 1 && (
                        <MenuItem disabled>Select a drive first</MenuItem>
                    )}
                    {
                        courses.filter((course) => course.id).map(
                            (course) => <MenuItem key={course.id} value={course.id} sx={checkboxOnlySelectedSx}>
                                <Checkbox size='small' checked={selectedCourseIds.includes(course.id)} />
                                <ListItemText primary={course.courseName} />
                            </MenuItem>
                        )
                    }
                </Select>
            </FormControl>

            <FormControl className='form-field' sx={{ width: '100%', minWidth: 0 }}>
                <InputLabel id="batchName">Batch Name</InputLabel>
                <Select
                    labelId="batchName"
                    id="batch"
                    label='Batch Name'
                    name='batch'
                    size='small'
                    value={selectedBatch?.id ?? 0}
                    onChange={(e, event) => handleBatchChange(event.props.value)}
                >
                    {
                        batches.map(
                            (batch) => <MenuItem key={batch.id} value={batch.id}>
                                {batch.batchName}
                            </MenuItem>
                        )
                    }
                </Select>
            </FormControl>

            <FormControl className='form-field' sx={{ width: '100%', minWidth: 0 }}>
                <InputLabel id="venueName" shrink>Venue</InputLabel>
                <Select
                    labelId="venueName"
                    id="venue"
                    label='Venue'
                    name='venue'
                    size='small'
                    multiple={!isSingleVenue}
                    displayEmpty
                    input={<OutlinedInput notched label='Venue' />}
                    value={isSingleVenue ? (selectedVenueIds[0] ?? '') : selectedVenueIds}
                    onChange={(e) => handleVenueChange(isSingleVenue ? [e.target.value] : e.target.value)}
                    renderValue={(value) => {
                        const ids = Array.isArray(value) ? value : (value ? [value] : []);
                        return ids.length
                            ? allData.venues.filter((venue) => ids.includes(venue.id)).map((venue) => venue.venueName).join(', ')
                            : 'None';
                    }}
                    MenuProps={{ PaperProps: { sx: { maxHeight: 320 } } }}
                >
                    {!venueOptions.length && (
                        <MenuItem disabled>No venues available</MenuItem>
                    )}
                    {
                        venueOptions.map(
                            // Single venue has no checkbox, so it keeps the normal highlighted row
                            (venue) => <MenuItem key={venue.id} value={venue.id} sx={isSingleVenue ? undefined : checkboxOnlySelectedSx}>
                                {!isSingleVenue && <Checkbox size='small' checked={selectedVenueIds.includes(venue.id)} />}
                                <ListItemText primary={venue.venueName} />
                            </MenuItem>
                        )
                    }
                </Select>
            </FormControl>
            <RadiusButton
                type='submit'
                variant='contained'
                color='secondary'
                onClick={onGo}
            >
                Go
            </RadiusButton>
        </Box>
    );
}
